/**
 * Track page analytics events (INTG-03). PHI-safe per C-06.
 * PostHog preserves native property types — `eventCount` is sent and stored as
 * a number (unlike the prior Statsig path, which coerced metadata to strings).
 *
 * Payload interfaces deliberately exclude all PHI fields (title, notes, location,
 * medicationName, doctorName, etc.) — only enums, UUIDs, booleans, and counts
 * are permitted (T-36-PHI-ANALYTICS-FOUNDATION).
 */
import type { EventType } from "../../types/event";

export const TRACK_PAGE_VIEWED = "track_page_viewed" as const;
export const TRACK_DAY_TAPPED = "track_day_tapped" as const;
export const TRACK_EVENT_OPENED = "track_event_opened" as const;

// Fired when the Coming Up day stepper (prev/next chevrons) moves the day.
// PHI-safe: a direction enum + an ISO date string (dates are not PHI per C-06).
export const TRACK_DAY_STEP = "track_day_step" as const;

export interface TrackDayStepPayload {
	direction: "prev" | "next";
	dayIso: string; // YYYY-MM-DD
}

// M011 S04 T05 — fired when a journey-derived task occurrence is tapped from
// the calendar or Coming Up. PHI-safe: taskId/stepId are static journey JSON
// keys (e.g. "J-02.S-01.SS-01.T-01"), not patient-identifiable values.
export const TRACK_OCCURRENCE_CLICKED = "track_occurrence_clicked" as const;

export interface TrackOccurrenceClickedPayload {
	taskId: string;
	stepId: string;
}

// Phase 36.1 D-07/D-08 (EventDetailSheet redesign).
// Both events use enum-only payload — eventId/baseId intentionally excluded
// because the topic-tap signal is not user-identifiable (CONTEXT.md C-06).
export const TRACK_EVENT_WHAT_TO_EXPECT_TAPPED =
	"track_event_what_to_expect_tapped" as const;
export const TRACK_EVENT_ASK_EVE_TAPPED = "track_event_ask_eve_tapped" as const;

export type TrackPageViewedPayload = undefined; // no payload

export interface TrackDayTappedPayload {
	dayIso: string; // YYYY-MM-DD
	eventCount: number; // sent to PostHog as a native number
}

export interface TrackEventOpenedPayload {
	// Phase 37 WR-07: eventId removed to match the enums-only contract used by
	// TRACK_EVENT_CREATED|EDITED|DELETED. UUIDs were arguably not PHI but the
	// per-phase invariant aligns the whole track surface on enums + booleans.
	type: "medication" | "appointment" | "test" | "cycle" | "admin";
	isRecurring: boolean;
}

export interface TrackEventWhatToExpectTappedPayload {
	eventType: EventType;
	isRecurring: boolean;
}

export interface TrackEventAskEveTappedPayload {
	eventType: EventType;
	isRecurring: boolean;
}

// Phase 37 D-21 (Add Event flow — create / edit / delete lifecycle).
// Payload is enums + booleans only per C-08 / UI-SPEC §"PHI-safe analytics + toast contract".
// NEVER include: eventId, baseId, title, location, notes, medicationName,
// doctorName, contactId, dateTime, dosage, day-iso strings. Test-track.ts
// PHI gates assert zero matches against the denylist in logEvent(...) call sites.
export const TRACK_EVENT_CREATED = "track_event_created" as const;
export const TRACK_EVENT_EDITED = "track_event_edited" as const;
export const TRACK_EVENT_DELETED = "track_event_deleted" as const;

export interface TrackEventCreatedPayload {
	eventType: EventType;
	isRecurring: boolean;
	/** Set to `true` when the server's idempotency replay path was taken (POST 200 cache-replay). */
	idempotencyHit?: boolean;
}

export interface TrackEventEditedPayload {
	eventType: EventType;
	isRecurring: boolean;
}

export interface TrackEventDeletedPayload {
	eventType: EventType;
	isRecurring: boolean;
}
